import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { BellRing, Smartphone, LogOut, Lock, Send, Download, Moon, Sun, SunMoon, Type, Fingerprint } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Switch } from "@/components/ui/switch";
import { useMobile, useMobileTheme } from "./MobileApp";
import { Glass, SectionTitle, TIPO_UI } from "./ui";
import { disableMobilePush, enableMobilePush, isIOS, isStandalone, mobilePushStatus } from "./push";

const Row = ({ children, testId }) => <div data-testid={testId} className="flex items-center justify-between gap-3 py-3 border-b border-white/5 last:border-0">{children}</div>;
const num = "w-28 h-9 rounded-xl bg-[#050912] border border-white/10 px-3 text-right font-mono text-sm outline-none focus:border-cyan-400/50";

const Push = () => {
  const [st, setSt] = useState(null);
  const refresh = () => mobilePushStatus().then(setSt);
  useEffect(() => { refresh(); }, []);
  const toggle = async () => {
    try {
      if (st?.subscribed) { await disableMobilePush(); toast.success("Notificaciones desactivadas en este equipo"); }
      else { await enableMobilePush(); toast.success("Notificaciones activadas"); }
    } catch (e) { toast.error(e.message); }
    refresh();
  };
  const probar = async () => {
    const { data } = await api.post("/mobile/alertas/prueba");
    toast.success(data.push_enviados ? "Enviamos una notificación de prueba" : "Alerta de prueba creada (activa las notificaciones para recibirla en el celular)");
  };
  return (
    <Glass className="p-4" data-testid="ajustes-push">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-2xl grid place-items-center bg-cyan-500/10 border border-cyan-500/30 text-cyan-300"><BellRing className="w-4 h-4" /></div>
        <div className="flex-1">
          <p className="text-sm font-semibold">Notificaciones en este celular</p>
          <p className="text-xs text-slate-400 mt-0.5" data-testid="ajustes-push-estado">
            {!st ? "Revisando…" : !st.supported ? (isIOS() && !isStandalone() ? "En iPhone, instala primero la app (abajo)." : "Este navegador no soporta push.") : st.subscribed ? "Activas" : st.permission === "denied" ? "Bloqueadas en el navegador" : "Desactivadas"}
          </p>
        </div>
        <Switch data-testid="ajustes-push-toggle" checked={!!st?.subscribed} onCheckedChange={toggle} disabled={!st?.supported} />
      </div>
      <button onClick={probar} data-testid="ajustes-push-probar" className="mt-3 w-full h-10 rounded-xl border border-white/10 text-xs font-semibold flex items-center justify-center gap-2 hover:bg-white/5 transition-colors duration-200">
        <Send className="w-3.5 h-3.5" /> Enviar alerta de prueba
      </button>
    </Glass>
  );
};

const Instalar = () => {
  const [evt, setEvt] = useState(null);
  useEffect(() => {
    const h = (e) => { e.preventDefault(); setEvt(e); };
    window.addEventListener("beforeinstallprompt", h);
    return () => window.removeEventListener("beforeinstallprompt", h);
  }, []);
  if (isStandalone()) return null;
  return (
    <Glass className="p-4" data-testid="ajustes-instalar">
      <p className="text-sm font-semibold flex items-center gap-2"><Smartphone className="w-4 h-4 text-cyan-300" />Instala Ai-POS en tu celular</p>
      {evt ? (
        <button onClick={() => evt.prompt()} data-testid="ajustes-instalar-btn" className="mt-3 w-full h-10 rounded-xl bg-cyan-500 text-[#04121A] text-sm font-bold flex items-center justify-center gap-2"><Download className="w-4 h-4" />Instalar app</button>
      ) : (
        <p className="text-xs text-slate-400 mt-2 leading-relaxed">
          {isIOS() ? "En Safari toca Compartir → \"Agregar a pantalla de inicio\"." : "En Chrome abre el menú ⋮ → \"Instalar app\" o \"Agregar a pantalla principal\"."}
        </p>
      )}
    </Glass>
  );
};

const TEMAS = [["dark", "Oscuro IA", Moon], ["light", "Claro", Sun], ["auto", "Automático", SunMoon]];

const Apariencia = () => {
  const { tema, setTema, grande, setGrande } = useMobileTheme();
  return (
    <Glass className="p-4" data-testid="ajustes-apariencia">
      <div className="grid grid-cols-3 gap-2">
        {TEMAS.map(([k, l, I]) => (
          <button key={k} onClick={() => setTema(k)} data-testid={`ajustes-tema-${k}`}
                  className={`rounded-2xl border py-3 flex flex-col items-center gap-1.5 text-xs font-semibold transition-colors duration-200 ${tema === k ? "border-cyan-400/40 bg-cyan-500/20 text-cyan-300" : "border-white/10 text-slate-400"}`}>
            <I className="w-5 h-5" />{l}
          </button>
        ))}
      </div>
      <Row testId="ajustes-texto-grande-row">
        <span className="flex items-center gap-2.5 text-sm text-slate-200"><Type className="w-4 h-4 text-slate-500" />Texto grande</span>
        <Switch data-testid="ajustes-texto-grande" checked={grande} onCheckedChange={setGrande} />
      </Row>
    </Glass>
  );
};

export default function Ajustes() {
  const { me, reload } = useMobile();
  const { logout } = useAuth();
  const nav = useNavigate();
  const [p, setP] = useState(me.prefs);
  const save = async (next) => {
    setP(next);
    try { await api.put("/mobile/prefs", next); reload(); } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo guardar"); }
  };
  const setAlerta = (t, v) => save({ ...p, alertas: { ...p.alertas, [t]: v } });
  const setSuc = (sid, on) => save({ ...p, sucursales_off: on ? p.sucursales_off.filter((x) => x !== sid) : [...p.sucursales_off, sid] });
  const setUmb = (k, v) => setP({ ...p, umbrales: { ...p.umbrales, [k]: v } });
  const setSil = (k, v) => save({ ...p, silencio: { ...p.silencio, [k]: v } });

  return (
    <div className="pt-5" data-testid="mobile-ajustes">
      <h1 className="text-2xl font-bold font-display">Ajustes</h1>
      <p className="text-xs text-slate-400 mt-1">{me.user.name} · {me.user.email}</p>
      <div className="mt-5 space-y-3"><Push /><Instalar /></div>

      <SectionTitle>Apariencia</SectionTitle>
      <Apariencia />

      <SectionTitle>Alertas que quiero recibir</SectionTitle>
      <Glass className="px-4" data-testid="ajustes-alertas">
        {me.tipos.map((t) => {
          const bloqueada = t.pro && me.tier !== "PRO";
          const I = TIPO_UI[t.tipo]?.icon || BellRing;
          return (
            <Row key={t.tipo} testId={`ajustes-alerta-row-${t.tipo}`}>
              <span className={`flex items-center gap-2.5 text-sm ${bloqueada ? "text-slate-500" : "text-slate-200"}`}><I className="w-4 h-4 text-slate-500" />{t.label}{bloqueada && <Lock className="w-3 h-3 text-amber-400" />}</span>
              <Switch data-testid={`ajustes-alerta-${t.tipo}`} disabled={bloqueada} checked={!bloqueada && !!p.alertas[t.tipo]} onCheckedChange={(v) => setAlerta(t.tipo, v)} />
            </Row>
          );
        })}
      </Glass>

      <SectionTitle>Umbrales</SectionTitle>
      <Glass className="px-4" data-testid="ajustes-umbrales">
        {[["cuadratura", "Avisar descuadre mayor a"], ["egreso", "Avisar egresos desde"], ...(me.tier === "PRO" ? [["venta_grande", "Venta grande desde"]] : [])].map(([k, l]) => (
          <Row key={k}><span className="text-sm text-slate-200">{l}</span>
            <input type="number" min={0} data-testid={`ajustes-umbral-${k}`} value={p.umbrales[k] ?? 0} onChange={(e) => setUmb(k, e.target.value)} onBlur={() => save(p)} className={num} /></Row>
        ))}
      </Glass>

      {me.stores.length > 1 && (<>
        <SectionTitle>Sucursales</SectionTitle>
        <Glass className="px-4" data-testid="ajustes-sucursales">
          {me.stores.map((s) => (
            <Row key={s.store_id}><span className="text-sm text-slate-200 truncate">{s.nombre}</span>
              <Switch data-testid={`ajustes-sucursal-${s.store_id}`} checked={!p.sucursales_off.includes(s.store_id)} onCheckedChange={(v) => setSuc(s.store_id, v)} /></Row>
          ))}
        </Glass>
      </>)}

      <SectionTitle>Horario</SectionTitle>
      <Glass className="px-4" data-testid="ajustes-horario">
        <Row><span className="text-sm text-slate-200">Resumen del día a las</span>
          <input type="time" data-testid="ajustes-hora-resumen" value={p.hora_resumen} onChange={(e) => save({ ...p, hora_resumen: e.target.value })} className={num} /></Row>
        <Row><span className="text-sm text-slate-200">Silenciar de noche</span>
          <Switch data-testid="ajustes-silencio" checked={!!p.silencio.activo} onCheckedChange={(v) => setSil("activo", v)} /></Row>
        {p.silencio.activo && (
          <Row><span className="text-xs text-slate-400">Sin notificaciones entre</span>
            <span className="flex items-center gap-1.5">
              <input type="time" data-testid="ajustes-silencio-desde" value={p.silencio.desde} onChange={(e) => setSil("desde", e.target.value)} className={`${num} w-[92px]`} />
              <input type="time" data-testid="ajustes-silencio-hasta" value={p.silencio.hasta} onChange={(e) => setSil("hasta", e.target.value)} className={`${num} w-[92px]`} />
            </span></Row>
        )}
      </Glass>

      <p className="mt-6 text-[11px] text-slate-500 text-center">Plan {me.tier === "PRO" ? "Pro" : "Start"} · hasta {me.limite_por_sucursal} usuario(s) de la app por sucursal</p>
      <button onClick={() => nav("/movil/seguridad")} data-testid="ajustes-seguridad"
              className="mt-3 w-full h-11 rounded-2xl border border-white/10 text-sm font-semibold flex items-center justify-center gap-2 hover:bg-white/5 transition-colors duration-200">
        <Fingerprint className="w-4 h-4 text-indigo-300" /> Huella y mis dispositivos
      </button>
      <button onClick={logout} data-testid="ajustes-logout" className="mt-3 mb-4 w-full h-11 rounded-2xl border border-rose-500/30 text-rose-300 text-sm font-semibold flex items-center justify-center gap-2 hover:bg-rose-500/10 transition-colors duration-200">
        <LogOut className="w-4 h-4" /> Cerrar sesión
      </button>
    </div>
  );
}
